import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function RivetCorners({
  className = ""
}: {
  className?: string;
}) {
  return <>
      <span className={`absolute top-3 left-3 w-1.5 h-1.5 bg-slate-400 ${className}`} style={{
      backgroundColor: "hsl(215 8% 70%)"
    }} aria-hidden="true" />
      <span className={`absolute top-3 right-3 w-1.5 h-1.5 ${className}`} style={{
      backgroundColor: "hsl(215 8% 70%)"
    }} aria-hidden="true" />
      <span className={`absolute bottom-3 left-3 w-1.5 h-1.5 ${className}`} style={{
      backgroundColor: "hsl(215 8% 70%)"
    }} aria-hidden="true" />
      <span className={`absolute bottom-3 right-3 w-1.5 h-1.5 ${className}`} style={{
      backgroundColor: "hsl(215 8% 70%)"
    }} aria-hidden="true" />
    </>;
}
function formatClockStamp(dateStr?: string): string {
  if (!dateStr) return "IDN · ARSIP";
  try {
    const d = new Date(dateStr);
    const months = ["JAN", "FEB", "MAR", "APR", "MEI", "JUN", "JUL", "AGU", "SEP", "OKT", "NOV", "DES"];
    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `IDN · ${day} ${month} ${year}`;
  } catch {
    return "IDN · ARSIP";
  }
}
function getCategoryLabel(post: any): string {
  if (post?.categories && post.categories.length > 0) {
    return String(post.categories[0].name || "").toUpperCase();
  }
  return "BERITA TERKINI";
}
function BlogPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="bg-card border border-border/40 animate-pulse">
            <div className="aspect-[4/3] bg-muted/40" />
            <div className="p-6 space-y-4">
              <div className="h-3 w-24 bg-muted/40" />
              <div className="h-6 w-full bg-muted/40" />
              <div className="h-6 w-3/4 bg-muted/40" />
              <div className="h-4 w-full bg-muted/40" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border/40 p-8 text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
          Arsip saat ini tidak tersedia
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-border/40 p-8 text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
          Belum ada berita yang diterbitkan
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {posts.slice(0, 3).map((post, i) => <article data-index={i} key={post.id} className="group relative bg-card border border-border/40 transition-all duration-300 hover:border-primary hover:-translate-y-0.5">
          <div className="relative overflow-hidden aspect-[4/3] bg-muted/30">
            {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-105" style={{
          filter: "grayscale(60%) contrast(1.1)"
        }} /> : <div className="w-full h-full flex items-center justify-center bg-muted/30">
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  TANPA GAMBAR
                </span>
              </div>}
            <RivetCorners />
          </div>

          <div className="relative p-6 md:p-7">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2" style={{
              backgroundColor: "hsl(25 83% 55%)"
            }} aria-hidden="true" />
                <span className="font-mono text-[11px] font-medium uppercase tracking-[0.18em]" style={{
              color: "hsl(25 83% 55%)"
            }}>
                  {getCategoryLabel(post)}
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">
                {formatClockStamp(post.date)}
              </span>
            </div>

            <h3 className="font-default text-xl md:text-2xl font-bold uppercase leading-[1.0] tracking-tight text-foreground mb-4 line-clamp-3">
              {post.title}
            </h3>

            {post.excerpt && <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-6 line-clamp-2" dangerouslySetInnerHTML={{
          __html: post.excerpt.replace(/<[^>]*>/g, "")
        }} />}

            <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.18em] pb-1 border-b-2 transition-colors" style={{
          color: "hsl(25 83% 55%)",
          borderColor: "hsl(25 83% 55%)"
        }}>
              BACA SELENGKAPNYA
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </article>)}
    </div>;
}
export default function FeaturedArticle() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1780" id="featured-article" className="bg-background text-foreground relative py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-3 opacity-30" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(0 0% 0%) 0px, hsl(0 0% 0%) 4px, hsl(0 0% 10%) 4px, hsl(0 0% 10%) 8px)"
    }} aria-hidden="true" />

      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-20">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2 h-2" style={{
              backgroundColor: "hsl(25 83% 55%)"
            }} aria-hidden="true" />
              <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em]" style={{
              color: "hsl(25 83% 55%)"
            }}>
                № 01 / ARTIKEL UTAMA
              </span>
            </div>

            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] font-extrabold uppercase leading-[0.92] tracking-tight text-foreground">
              LAPORAN
              <br />
              <span className="relative inline-block">
                TERKINI{" "}
                <span className="relative inline-block">
                  HARIAN
                  <span className="absolute left-0 right-0 bottom-1 h-1 -rotate-1" style={{
                  backgroundColor: "hsl(25 83% 55%)",
                  opacity: 0.6
                }} aria-hidden="true" />
                </span>
              </span>
            </h2>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/NewsPage" className="inline-flex items-center gap-2 font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] pb-1.5 border-b-2 transition-colors text-foreground" style={{
            borderColor: "hsl(25 83% 55%)"
          }}>
              LIHAT SEMUA BERITA
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <BlogPostsConsumer />
        </PostsProvider>

        <div className="mt-12 md:mt-16 flex justify-center lg:hidden">
          <Link to="/NewsPage">
            <Button variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs font-bold uppercase tracking-[0.12em] px-7 py-6 border-2 border-foreground">
              LIHAT SEMUA BERITA
              <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2.5} />
            </Button>
          </Link>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-3 opacity-30" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(0 0% 0%) 0px, hsl(0 0% 0%) 4px, hsl(0 0% 10%) 4px, hsl(0 0% 10%) 8px)"
    }} aria-hidden="true" />
    </section>;
}